import { Component, ElementRef, EventEmitter, HostListener, OnInit, OnDestroy, Output, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { DealerPortalService } from 'src/app/services/dealerPortal.service';
import { environment } from 'src/environments/environment';
import { SharedDataService } from 'src/app/services/sharedDataService.service';
import { CustomerService } from 'src/app/services/customer.service';

interface PromotionAwardVO {
  bizType: string;
  bizId: string;
  bizName: string;
  sendQty: number;
  imgUrl?: string;
}

interface PromotionActivityMechanismVO {
  type: string;
  value: number;
  award: PromotionAwardVO;
}

export interface PromotionActivitySummary {
  activityId: number;
  activityName: string;
  activityContents: string;
  startTime: string;
  endTime: string;
  /** 奖券图片文件 ID（后台 750*400 裁切上传），由 activity-summary 接口返回 */
  awardImgUrl?: string;
  baselineType: string;
  activityMechanism: PromotionActivityMechanismVO;
  awardDistributionTimeliness: string;
  disclaimer: string;
  issuedAwardQty: number;
  redeemedAwardQty: number;
  progressCurrent: number;
  progressTarget: number;
  progressRemaining: number;
}

@Component({
  selector: 'app-promotion-popup-window',
  templateUrl: './promotion-popup-window.component.html',
  styleUrls: ['./promotion-popup-window.component.less']
})
export class PromotionPopupWindowComponent implements OnInit, OnDestroy {
  @Output() modalClosed = new EventEmitter<void>();

  isVisible = false;
  activityList: PromotionActivitySummary[] = [];
  activeSlideIndex = 0;
  isDealerPortal: boolean = false;
  /** 奖券图加载失败的活动 ID 集合，模板据此回退为图标占位（180*180 方图铺在 750*400 横版槽内不美观，不再使用） */
  awardImgBroken = new Set<number>();
  /** 活动详情正文超过 4 行、需要展示"更多"的活动 ID 集合 */
  contentOverflowIds = new Set<number>();
  /** 已展开详情正文的活动 ID 集合 */
  expandedContentIds = new Set<number>();

  /** 详情正文段落，用于测量是否超过 4 行 */
  @ViewChildren('detailContent') detailContents!: QueryList<ElementRef<HTMLElement>>;
  /** 轮播 slide 元素，用于测量当前 slide 的布局高度 */
  @ViewChildren('promoSlide') promoSlides!: QueryList<ElementRef<HTMLElement>>;

  /**
   * 轮播可视区高度（px），跟随当前 slide 的内容高度。
   * 所有 slide 横向并排在同一个 flex 轨道里，轨道高度由最高的 slide 撑开，
   * 若不单独限制可视区高度，内容少的活动在"责任声明"下方就会露出大块空白。
   */
  carouselHeight: number | null = null;

  // 退出登录会执行 localStorage.clear()，因此"当天是否已弹出"的标记改用 cookie 存储；
  // cookie 名带客户编码，保证每个客户各自维护"一天只展示一次"，互不覆盖
  private readonly STORAGE_KEY = 'promo_popup_last_shown';
  private autoPlayTimer: any = null;
  private customerSubscription: Subscription | null = null;
  /** 当前弹窗数据对应的客户编码，用于识别客户切换 */
  private currentCustomerCode: string | null = null;
  /** 请求令牌：客户切换后丢弃旧客户还在途中的过期响应 */
  private loadToken = 0;
  /** 轮播高度测量定时器，延后到本轮变更检测之后测量 */
  private heightMeasureTimer: any = null;

  constructor(
    private router: Router,
    private dealerPortalService: DealerPortalService,
    private sharedData: SharedDataService,
    private customerService: CustomerService
  ) {
    this.sharedData.currentData.subscribe(data => {
      this.isDealerPortal = data.isDealerPortal;
    });
  }

  ngOnInit(): void {
    // 促销活动汇总是客户维度接口，未登录时不请求，也不消耗当日弹出额度
    if (!this.isLoggedIn()) {
      this.emitClosed();
      return;
    }
    // selectedCustomer$ 是 BehaviorSubject，订阅时会立即回放当前客户，
    // 因此首屏等待客户就绪与后续切换客户共用同一条链路
    this.customerSubscription = this.customerService.selectedCustomer$.subscribe(customer => {
      this.handleCustomerChange(customer?.customerCode ?? null);
    });
  }

  ngOnDestroy(): void {
    this.stopAutoPlay();
    clearTimeout(this.heightMeasureTimer);
    this.customerSubscription?.unsubscribe();
  }

  /**
   * 客户变更处理：首次客户就绪、切换客户都走这里。
   * 切换时先收起当前弹窗并清空旧客户数据，再按新客户重新请求内容，
   * 并校验该客户当天是否已展示过。
   */
  private handleCustomerChange(customerCode: string | null): void {
    if (!customerCode || customerCode === this.currentCustomerCode) {
      return;
    }
    this.currentCustomerCode = customerCode;
    // 丢弃可能还在途中的旧客户请求
    this.loadToken++;
    this.stopAutoPlay();
    this.isVisible = false;
    this.activityList = [];
    this.activeSlideIndex = 0;
    this.awardImgBroken.clear();
    this.contentOverflowIds.clear();
    this.expandedContentIds.clear();
    this.carouselHeight = null;

    if (!this.isLoggedIn()) {
      this.emitClosed();
      return;
    }
    // 该客户今天已看过则不再请求、不再弹出
    if (this.isAlreadyShownToday(customerCode)) {
      this.emitClosed();
      return;
    }
    this.loadActivitySummary();
  }

  handleClose(): void {
    this.stopAutoPlay();
    this.isVisible = false;
    // 仅在用户主动关闭或点击"立即参与"时，才记录该客户今日已展示
    if (this.currentCustomerCode) {
      this.markShownToday(this.currentCustomerCode);
    }
    this.emitClosed();
  }

  goToPromotionList(): void {
    const country = localStorage.getItem('country') || 'cn';
    this.router.navigate([`/${country}/promotion-activity`]);
    this.handleClose();
    if (environment.production) {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const customer = JSON.parse(localStorage.getItem('customer') || '{}');
      (window as any).dataLayer.push({
        event: 'dp_promotion_popup_view',
        from_page:
          (localStorage.getItem('token') ? 'afterLogin ' : 'beforeLogin ') +
          window.location.pathname,
        event_type: '',
        event_name: '',
        portal_name: this.isDealerPortal
          ? 'DealerPortal ' + customer?.customerCode
          : 'SalesPortal ' + userData?.queryUserPageInfoResDTO?.ntAccount,
        event_content: (this.isDealerPortal
          ? customer?.customerCode
          : userData?.queryUserPageInfoResDTO?.ntAccount) +
          ' ' + (this.activity?.activityId ?? ''),
      });
    }
  }

  /** 请求活动内容，响应返回时校验请求令牌，丢弃切换客户前发出的过期响应 */
  private loadActivitySummary(): void {
    const token = ++this.loadToken;
    this.dealerPortalService.getPromotionActivitySummary({ type: 'popwindow' }).subscribe({
      next: (res) => {
        // 请求返回前客户已切换，丢弃旧客户的响应
        if (token !== this.loadToken) {
          return;
        }
        // 响应到达时登录态可能已失效（如残留 token 过期，拦截器会清空 localStorage 并跳转），此时不弹窗
        if (!this.isLoggedIn()) {
          this.emitClosed();
          return;
        }
        // 非成功响应（如 10001 token 失效）的 rtnData 可能是非数组形态，一律不弹窗
        if (res?.rtnCode !== 10000 || !Array.isArray(res?.rtnData)) {
          this.emitClosed();
          return;
        }
        const list: PromotionActivitySummary[] = res.rtnData.filter((item: PromotionActivitySummary) => !!item);
        this.activityList = list;
        this.awardImgBroken.clear();
        this.activeSlideIndex = 0;
        // 无数据则不弹窗，直接关闭
        if (list.length === 0) {
          this.emitClosed();
          return;
        }
        // 展示时不写 cookie，待用户关闭弹窗或点击"立即参与"后再记录（见 handleClose）
        this.isVisible = true;
        // 弹窗渲染后立即把可视区收紧到首个活动的高度，避免开场先按最高 slide 撑满再回缩
        this.scheduleMeasureCarouselHeight();
        if (list.length > 1) { this.startAutoPlay(); }
      },
      error: () => {
        // 客户已切换时不要关闭新客户的弹窗
        if (token !== this.loadToken) {
          return;
        }
        this.emitClosed();
      },
    });
  }

  get activity(): PromotionActivitySummary | null {
    return this.activityList[this.activeSlideIndex] ?? null;
  }

  /** 展开/收起详情正文 */
  toggleContent(activityId: number): void {
    if (this.expandedContentIds.has(activityId)) {
      this.expandedContentIds.delete(activityId);
    } else {
      this.expandedContentIds.add(activityId);
    }
    // 正文展开/收起会改变 slide 高度，可视区高度需要跟着调整
    this.scheduleMeasureCarouselHeight();
  }

  /** 弹窗打开动画结束后再测量，此时内容已完成布局 */
  onModalAfterOpen(): void {
    this.measureContentOverflow();
    this.scheduleMeasureCarouselHeight();
  }

  /** 窗口尺寸变化会改变正文换行行数，重新对齐当前 slide 高度 */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleMeasureCarouselHeight();
  }

  /** 测量每个活动正文是否超过 4 行，只有超出的才展示"更多" */
  private measureContentOverflow(): void {
    this.contentOverflowIds.clear();
    const activities = this.activityList;
    this.detailContents?.forEach((ref, index) => {
      const activity = activities[index];
      if (!activity) {
        return;
      }
      // 已展开时高度就是完整高度，沿用之前“超出 4 行”的结论，保证"收起"仍可见
      if (this.expandedContentIds.has(activity.activityId)) {
        this.contentOverflowIds.add(activity.activityId);
        return;
      }
      const el = ref.nativeElement;
      // 临时解除行数限制，拿完整高度与截断后的高度对比
      const style = el.style as any;
      const previousClamp = style.webkitLineClamp;
      style.webkitLineClamp = 'unset';
      const fullHeight = el.scrollHeight;
      style.webkitLineClamp = previousClamp;
      if (fullHeight > el.clientHeight + 1) {
        this.contentOverflowIds.add(activity.activityId);
      }
    });
  }

  get hasMultipleActivities(): boolean {
    return this.activityList.length > 1;
  }

  goToSlide(index: number): void {
    this.setActiveSlide(index);
    this.resetAutoPlay();
  }

  prevSlide(): void {
    this.setActiveSlide((this.activeSlideIndex - 1 + this.activityList.length) % this.activityList.length);
    this.resetAutoPlay();
  }

  nextSlide(): void {
    this.setActiveSlide((this.activeSlideIndex + 1) % this.activityList.length);
    this.resetAutoPlay();
  }

  private startAutoPlay(): void {
    this.autoPlayTimer = setInterval(() => {
      this.setActiveSlide((this.activeSlideIndex + 1) % this.activityList.length);
    }, 10000);
  }

  private stopAutoPlay(): void {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  private resetAutoPlay(): void {
    this.stopAutoPlay();
    if (this.activityList.length > 1) { this.startAutoPlay(); }
  }

  get slideTrackStyle(): { transform: string } {
    return { transform: `translateX(-${this.activeSlideIndex * 100}%)` };
  }

  /** 切换当前 slide，并把轮播可视区高度对齐到新 slide 的内容高度 */
  private setActiveSlide(index: number): void {
    this.activeSlideIndex = index;
    this.scheduleMeasureCarouselHeight();
  }

  /** 延后到本轮变更检测之后测量：slide 切换、正文展开收起、弹窗打开都要重新量一次 */
  private scheduleMeasureCarouselHeight(): void {
    clearTimeout(this.heightMeasureTimer);
    this.heightMeasureTimer = setTimeout(() => this.measureCarouselHeight());
  }

  private measureCarouselHeight(): void {
    const slide = this.promoSlides?.get(this.activeSlideIndex)?.nativeElement;
    if (!slide) {
      return;
    }
    // 弹窗未打开时 slide 没有布局高度（0），保持原值，避免把容器压扁
    const height = this.readLayoutHeight(slide);
    if (height > 0 && height !== this.carouselHeight) {
      this.carouselHeight = height;
    }
  }

  /**
   * 读取元素的布局高度：统一用 getComputedStyle 的 used 值，与 inline height 处于同一坐标系；
   * 弹窗整体 zoom 0.85 时 offsetHeight 返回的是缩放后的视觉值，直接赋值会再缩放一次。
   */
  private readLayoutHeight(el: HTMLElement): number {
    const computed = parseFloat(getComputedStyle(el).height);
    return Number.isFinite(computed) ? computed : el.offsetHeight;
  }

  /** 判断是否已登录：userData 可能残留在 localStorage，只有 token 代表本次会话有效 */
  private isLoggedIn(): boolean {
    return !!localStorage.getItem('token');
  }

  /** 判断该客户今天是否已经弹出过 */
  private isAlreadyShownToday(customerCode: string): boolean {
    return this.readShownDate(this.storageKeyOf(customerCode)) === moment().format('YYYY-MM-DD');
  }

  /** 记录该客户今天已弹出 */
  private markShownToday(customerCode: string): void {
    this.writeShownDate(this.storageKeyOf(customerCode), moment().format('YYYY-MM-DD'));
  }

  /** 每个客户使用独立的 cookie key，互不覆盖 */
  private storageKeyOf(customerCode: string): string {
    return `${this.STORAGE_KEY}_${encodeURIComponent(customerCode)}`;
  }

  /** 从 cookie 读取指定客户的上次弹出日期（localStorage.clear() 不会清除 cookie） */
  private readShownDate(key: string): string | null {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(
      new RegExp('(?:^|;\\s*)' + escapedKey + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  /** 将指定客户的上次弹出日期写入 cookie，过期时间设为当天结束 */
  private writeShownDate(key: string, date: string): void {
    const expires = moment().endOf('day').toDate().toUTCString();
    document.cookie = `${key}=${encodeURIComponent(date)}; expires=${expires}; path=/`;
  }

  private emitClosed(): void {
    this.modalClosed.emit();
  }

  /** 格式化活动时间范围，如 "2024年3月1日 至 2024年4月30日" */
  formatTimeRange(activity: PromotionActivitySummary): string {
    const start = this.formatDateCN(activity.startTime);
    const end = this.formatDateCN(activity.endTime);
    return `${start} 至 ${end}`;
  }

  /** 核心优惠描述 — 根据 activityMechanism 动态拼装 */
  formatCoreBenefitDesc(activity: PromotionActivitySummary): string {
    if (!activity?.activityMechanism) { return ''; }
    const m = activity.activityMechanism;
    const typeLabel = m.type === 'Each' ? '每' : '累计';
    const valueLabel = `${m.value}`;
    const awardName = m.award?.bizName ?? '';
    const sendQty = m.award?.sendQty ?? 1;
    const suffix = m.type === 'Each' ? '，多买多送' : '';
    return `${typeLabel}采购达${valueLabel}元送${awardName}${sendQty}个${suffix}。`;
  }

  /** 奖励发放描述 */
  formatAwardTimeliness(activity: PromotionActivitySummary): string {
    if (!activity?.awardDistributionTimeliness) {
      return '卡券将在订单确认收货后自动发放至您的账户。';
    }
    return activity.awardDistributionTimeliness;
  }

  /**
   * 活动奖券图片：图片 ID（awardImgUrl）走 userCenterAdmin 文件下载接口换取图片，
   * 与 promotion-activity / new-feature-guide / banner 同一取图方式；无 ID 时返回空串，由模板回退图标占位。
   */
  getAwardImageUrl(activity: PromotionActivitySummary): string {
    const fileId = activity?.awardImgUrl || activity?.activityMechanism?.award?.imgUrl;
    return fileId
      ? `${environment.userCenterAdminUrl}/file/download/${fileId}`
      : '';
  }

  /** 图片加载失败（文件 ID 失效或文件服务异常）时标记该活动，模板切换为图标占位 */
  onAwardImageError(activity: PromotionActivitySummary): void {
    this.awardImgBroken.add(activity.activityId);
  }

  private formatDateCN(dateStr: string): string {
    if (!dateStr) { return ''; }
    const m = moment(dateStr);
    return `${m.year()}年${m.month() + 1}月${m.date()}日`;
  }
}
